import { Button, Slider } from '@heroui/react';
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiRotateCcw, FiRotateCw } from 'react-icons/fi';

export interface ImageEditorProps {
  isEditMode: boolean;
  onDrop: (files: File[]) => void;
  onScaleChange: (scale: number) => void;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
  scale: number;
  isDisabled?: boolean;
}

export const ImageEditor: React.FC<ImageEditorProps> = ({
  isEditMode,
  onDrop,
  onScaleChange,
  onRotateLeft,
  onRotateRight,
  scale,
  isDisabled = false,
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
    },
    multiple: false,
    disabled: isDisabled,
  });

  const handleScaleChange = useCallback(
    (value: number | number[]) => {
      const scaleValue = Array.isArray(value) ? value[0] : value;
      onScaleChange(scaleValue);
    },
    [onScaleChange]
  );

  return (
    <div className="space-y-4">
      {/* File Upload Area */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${
            isDragActive
              ? 'border-primary bg-primary-50'
              : 'border-default-300 bg-default-50 hover:border-default-400 hover:bg-default-100'
          }
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <div className="space-y-3">
          <div className="flex justify-center">
            <div className="p-3 bg-default-100 rounded-full">
              <FiUpload className="h-6 w-6 text-default-600" />
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {isDragActive
                ? 'Drop the image here'
                : 'Drag & Drop your Image file here'}
            </p>
            <p className="text-xs text-default-500">
              or click to select your file to upload
            </p>
          </div>
          <p className="text-xs text-default-400">
            Supports: JPEG, PNG, GIF, BMP, WebP
          </p>
        </div>
      </div>

      {/* Image Editing Controls - Only show when editing */}
      {isEditMode && (
        <div className="space-y-4 p-4 bg-content1 rounded-lg border border-default-200">
          <h4 className="text-sm font-medium text-foreground">
            Image Controls
          </h4>

          {/* Scale Slider */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">
              Zoom: {Math.round(scale * 100)}%
            </label>
            <Slider
              size="sm"
              step={0.01}
              minValue={0.1}
              maxValue={3}
              value={scale}
              onChange={handleScaleChange}
              className="max-w-md"
              classNames={{
                track: 'bg-default-300',
                filler: 'bg-primary',
                thumb: 'bg-primary border-2 border-white shadow-lg',
              }}
            />
          </div>

          {/* Rotation Controls */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">
              Rotation
            </label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="flat"
                startContent={<FiRotateCcw className="h-4 w-4" />}
                onPress={onRotateLeft}
              >
                Rotate Left
              </Button>
              <Button
                size="sm"
                variant="flat"
                startContent={<FiRotateCw className="h-4 w-4" />}
                onPress={onRotateRight}
              >
                Rotate Right
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
